/**
 * LED Customer Display Service
 *
 * Communicates with physical LED pole displays via serial port.
 * Uses ESC/POS protocol for communication.
 *
 * Display modes:
 * - unit: Unit price (单价)
 * - quantity: Quantity (数量)
 * - total: Total amount (总计)
 * - payment: Payment received (收款)
 * - change: Change due (找零)
 *
 * Display value options (user configurable):
 * - SALE_TOTAL: Shows the sale total during transactions
 * - PAID_AMOUNT: Shows the payment amount during transactions
 * - CHANGE: Shows the change amount during transactions
 */

import { Storage } from '@/services/Storage';
import LedConfig from '@/services/LedConfig';
// Centralised "should the renderer touch the LED COM port right now?" gate.
// See src/services/ledGate.js — fail-safe default is OFF so the serial
// port stays free for other programs whenever the user has not explicitly
// enabled the LED display.
import { isLedDisabled } from '@/services/ledGate';

// Device capability (checked once at load time)
const hasLedDisplay = !!(window.ledDisplay && window.ledDisplay.showWithMode);

const LedService = {
  /**
   * Check if LED display is available
   */
  isAvailable() {
    return hasLedDisplay;
  },

  /**
   * Get LED settings from storage
   */
  async getSettings() {
    const settings = await Storage.getDeviceSettings();
    const effective = LedConfig.getEffective(settings);
    return {
      port: effective?.ledPort || 'COM2',
      baudRate: effective?.ledBaudRate || 2400,
      enabled: effective?.enableLedDisplay || false,
      displayValue: effective?.ledDisplayValue || 'SALE_TOTAL'
    };
  },

  /**
   * Display price (simple display without mode indicator)
   * @param {number} price - Price to display (e.g., 123.45)
   */
  async showPrice(price) {
    if (!this.isAvailable()) {
      console.warn('[LED] LED display not available');
      return { success: false, error: 'LED display not available' };
    }

    // Off-switch: see showWithMode for rationale.
    try {
      if (isLedDisabled(await this.getSettings())) {
        console.log('[LED] showPrice skipped: LED display disabled in settings');
        return { success: false, error: 'LED display disabled' };
      }

      const formatted = parseFloat(price).toFixed(2);
      return await window.ledDisplay.show(formatted);
    } catch (err) {
      console.error(`[LED] Failed to show price: ${err}`);
      return { success: false, error: String(err) };
    }
  },

  /**
   * Display a value with a specific mode
   * @param {string} mode - Display mode (unit, quantity, total, payment, change)
   * @param {string|number} value - Value to display
   * @param {Object} options - Optional settings override
   */
  async showWithMode(mode, value, options = {}) {
    console.log(`[LED] showWithMode called - Mode: ${mode}, Value: ${value}`);

    if (!this.isAvailable()) {
      console.warn('[LED] LED display not available');
      return { success: false, error: 'LED display not available' };
    }

    // Get settings from storage or use options
    const settings = await this.getSettings();

    // Hard off-switch: when the user has disabled the LED display in
    // Settings -> LED Display -> "Enable LED Display", do not touch the
    // COM port at all so other programs can use the same serial port.
    if (isLedDisabled(settings)) {
      console.log('[LED] showWithMode skipped: LED display disabled in settings');
      return { success: false, error: 'LED display disabled' };
    }

    const port = options.port || settings.port;
    const baudRate = options.baudRate || parseInt(settings.baudRate) || 2400;

    console.log(`[LED] Using port: ${port}, baudRate: ${baudRate}`);

    try {
      const formattedValue = parseFloat(value).toFixed(2);
      console.log(`[LED] Sending to display - Mode: ${mode}, Formatted: ${formattedValue}`);
      const result = await window.ledDisplay.showWithMode(mode, formattedValue, port, baudRate);
      console.log(`[LED] Display result:`, result);
      return result;
    } catch (err) {
      console.error(`[LED] Failed to display ${mode}: ${err}`);
      return { success: false, error: String(err) };
    }
  },

  /**
   * Display unit price
   * @param {number} price - Unit price
   * @param {Object} options - Optional settings
   */
  async showUnitPrice(price, options = {}) {
    console.log(`[LED] showUnitPrice: ${price}`);
    return this.showWithMode('unit', price, options);
  },

  /**
   * Display quantity
   * @param {number} qty - Quantity
   * @param {Object} options - Optional settings
   */
  async showQuantity(qty, options = {}) {
    console.log(`[LED] showQuantity: ${qty}`);
    return this.showWithMode('quantity', qty.toString(), options);
  },

  /**
   * Display total amount
   * @param {number} total - Sale total
   * @param {Object} options - Optional settings
   */
  async showTotal(total, options = {}) {
    console.log(`[LED] showTotal: ${total}`);
    return this.showWithMode('total', total, options);
  },

  /**
   * Display payment amount received
   * @param {number} amount - Payment amount
   * @param {Object} options - Optional settings
   */
  async showPayment(amount, options = {}) {
    console.log(`[LED] showPayment: ${amount}`);
    return this.showWithMode('payment', amount, options);
  },

  /**
   * Display change due
   * @param {number} change - Change amount
   * @param {Object} options - Optional settings
   */
  async showChange(change, options = {}) {
    console.log(`[LED] showChange: ${change}`);
    return this.showWithMode('change', change, options);
  },

  /**
   * Clear the LED display
   * @param {Object} options - Optional settings
   */
  async clear(options = {}) {
    console.log('[LED] clear() called');

    if (!this.isAvailable()) {
      console.warn('[LED] LED display not available');
      return { success: false, error: 'LED display not available' };
    }

    const settings = await this.getSettings();

    // Off-switch: see showWithMode for rationale.
    if (isLedDisabled(settings)) {
      console.log('[LED] clear skipped: LED display disabled in settings');
      return { success: false, error: 'LED display disabled' };
    }

    const port = options.port || settings.port;
    const baudRate = options.baudRate || parseInt(settings.baudRate) || 2400;

    try {
      console.log('[LED] Sending clear command...');
      const result = await window.ledDisplay.clear(port, baudRate);
      console.log('[LED] Clear result:', result);
      return result;
    } catch (err) {
      console.error(`[LED] Failed to clear display: ${err}`);
      return { success: false, error: String(err) };
    }
  },

  /**
   * Display the selected value based on user's settings
   * This is called during payment transactions to show the user-configured value
   * @param {Object} transactionData - Transaction information
   * @param {number} transactionData.total - Sale total
   * @param {number} transactionData.paid - Amount paid
   * @param {number} transactionData.change - Change due
   * @param {Object} options - Optional settings override
   */
  async displaySelectedValue(transactionData, options = {}) {
    const { total, paid, change } = transactionData;
    
    // Get settings
    const settings = await this.getSettings();
    
    // Check if LED display is enabled
    if (!settings.enabled) {
      console.log('[LED] LED display is disabled in settings');
      return { success: false, error: 'LED display disabled' };
    }

    const opts = { 
      ...options, 
      port: options.port || settings.port, 
      baudRate: options.baudRate || parseInt(settings.baudRate) || 2400 
    };

    const displayValue = settings.displayValue || 'SALE_TOTAL';
    console.log(`[LED] displaySelectedValue - Setting: ${displayValue}, Total: ${total}, Paid: ${paid}, Change: ${change}`);

    switch (displayValue) {
      case 'SALE_TOTAL':
        console.log(`[LED] Displaying SALE_TOTAL: ${total}`);
        return this.showTotal(total, opts);
      
      case 'PAID_AMOUNT':
        console.log(`[LED] Displaying PAID_AMOUNT: ${paid}`);
        return this.showPayment(paid, opts);
      
      case 'CHANGE':
        console.log(`[LED] Displaying CHANGE: ${change}`);
        return this.showChange(change, opts);
      
      default:
        console.warn(`[LED] Unknown display value setting: ${displayValue}, defaulting to SALE_TOTAL`);
        return this.showTotal(total, opts);
    }
  },

  /**
   * Display payment transaction summary
   * @param {Object} paymentData - Payment information
   * @param {number} paymentData.total - Sale total
   * @param {number} paymentData.paid - Amount paid
   * @param {number} paymentData.change - Change due
   * @param {Object} options - Optional settings
   */
  async displayPaymentSummary(paymentData, options = {}) {
    const { total, paid, change } = paymentData;
    console.log(`[LED] Payment flow started - Total: ${total}, Paid: ${paid}, Change: ${change}`);

    // Get settings for consistent port/baudRate
    const settings = await this.getSettings();
    
    // Check if LED display is enabled
    if (!settings.enabled) {
      console.log('[LED] LED display is disabled in settings');
      return { success: false, error: 'LED display disabled' };
    }

    const opts = { ...options, port: options.port || settings.port, baudRate: options.baudRate || parseInt(settings.baudRate) || 2400 };

    // Display total first
    console.log('[LED] Step 1/3 - Displaying total...');
    await this.showTotal(total, opts);

    // Small delay before showing payment
    await new Promise(resolve => setTimeout(resolve, 500));

    // Show payment received
    console.log('[LED] Step 2/3 - Displaying payment...');
    await this.showPayment(paid, opts);

    // Small delay before showing change
    await new Promise(resolve => setTimeout(resolve, 500));

    // Show change due
    console.log('[LED] Step 3/3 - Displaying change...');
    await this.showChange(change, opts);

    console.log('[LED] Payment flow completed');
  },

  /**
   * Reset display to idle state (clears and shows total)
   * @param {number} currentTotal - Current sale total to display
   * @param {Object} options - Optional settings
   */
  async resetToIdle(currentTotal, options = {}) {
    console.log(`[LED] resetToIdle: ${currentTotal}`);

    await this.clear(options);

    if (currentTotal && currentTotal > 0) {
      console.log(`[LED] Displaying idle total: ${currentTotal}`);
      await this.showTotal(currentTotal, options);
    }
  }
};

export default LedService;
